/**
 * Paged reads of the Supabase `tasks` table.
 *
 * PostgREST silently caps any read that carries no Range header at the
 * project's "Max rows" setting (1 000 by default). Every planner surface used
 * to run `select('*').eq('user_id', …)` with no limit, so a heavy user (a few
 * daily recurring series over a year or two) received only their oldest
 * 1 000 rows and everything past a "cliff" date looked deleted on the next
 * refetch. Always read through this helper.
 */

export const TASKS_PAGE_SIZE = 1000;

/**
 * Fetch every task row for a user, ordered by date then id, paging until a
 * short page comes back. Returns the same `{ data, error }` shape as a plain
 * supabase-js query so call sites stay one-line swaps.
 */
export async function fetchAllTasks(supabase, userId, { pageSize = TASKS_PAGE_SIZE } = {}) {
  return fetchAllRows(supabase, 'tasks', userId, { orderBy: 'date', pageSize });
}

/**
 * Generic paged read of one of the user's planner tables (`tasks`,
 * `priorities`, `annual_goals`): every row for `userId`, ordered by `orderBy`
 * then `id`, paged until a short page. Same `{ data, error }` shape as a plain
 * supabase-js query.
 */
export async function fetchAllRows(supabase, table, userId, { select = '*', orderBy = 'id', pageSize = TASKS_PAGE_SIZE } = {}) {
  const rows = [];
  const seen = new Set();
  let from = 0;
  let total = null;
  // Hard stop far above any realistic list (25 pages = 25 000 rows) so a
  // misbehaving backend can never loop forever.
  for (let page = 0; page < 25; page += 1) {
    // The first page asks for the exact total (Content-Range) so termination
    // does not depend on the project's max-rows setting being >= pageSize.
    let q = supabase.from(table).select(select, page === 0 ? { count: 'exact' } : undefined).eq('user_id', userId);
    if (orderBy && orderBy !== 'id') q = q.order(orderBy, { ascending: true });
    // created_at keeps same-day rows in the order they were added (what users
    // saw before paging); id makes the page boundaries deterministic.
    const { data, error, count } = await q.order('created_at', { ascending: true }).order('id', { ascending: true }).range(from, from + pageSize - 1);
    if (error) return { data: null, error };
    if (page === 0 && typeof count === 'number') total = count;
    const got = data ? data.length : 0;
    if (got) {
      // A row inserted while we page shifts the offsets and can repeat a row
      // on the next page; keep the first copy so the list never shows twins.
      for (const row of data) {
        const key = row && row.id != null ? row.id : null;
        if (key !== null && seen.has(key)) continue;
        if (key !== null) seen.add(key);
        rows.push(row);
      }
    }
    if (got === 0) break;
    if (total !== null ? rows.length >= total : got < pageSize) break;
    from += got; // advance by what we received, not by pageSize
  }
  return { data: rows, error: null };
}
